import {Vector3} from 'three';
import {AsinTileData, KSavvyState} from 'src/store';
import {isFilteredOut} from 'src/filter/globalFilter';
import {Parametric} from "src/components/3d/AsinTile";


export type TileZone = 'FILTERED_OUT' | 'USA' | 'EARTH';


const MAX_VISIBLE_TILES = 1000;

export function cullOldTiles(allTiles: AsinTileData[], state: KSavvyState) {
  const numVisibleTiles = allTiles.filter((tile) => !isFilteredOut(tile, state)).length;
  const culled = [];
  const now = new Date().getTime();
  const minCullAge = now - 10000; // 10 seconds
  if (numVisibleTiles > MAX_VISIBLE_TILES) {
    const numToCull = numVisibleTiles - MAX_VISIBLE_TILES;
    for (let i = 0; culled.length < numToCull && i < allTiles.length;) {
      if (!isFilteredOut(allTiles[i], state) && allTiles[i].dateAdded.getTime() < minCullAge) {
        culled.push(...allTiles.splice(i, 1));
      } else {
        i++;
      }
    }
  }
  return culled;
}

function randFloat(low: number, high: number): number {
  return Math.random() * (high - low) + low;
}

export function getEntranceWaypoint(): Vector3 {
  return new Vector3(
    randFloat(-80, 80),
    randFloat(250, 300),
    randFloat(400, 420),
  );
}

export function arrangeEarthClusterTiles(allTiles: AsinTileData[], clusterName: string) {
  const tilesInCluster = allTiles
      .filter((entry) => entry.animationData.cluster!.name === clusterName)
      .filter((entry) => entry.animationData.phase !== 'ENTRANCE');

  tilesInCluster.forEach((entry, index) => {
    entry.animationData.parametricTargetValue = tilesInCluster.length - index - 1;
  });
}

/**
 * Returns a parametric function that takes in t and outputs a Vector3.
 * The intended use of the parametric function is to guide the motion of an object as it
 * moves toward a target value of t, rather than relying on linear interpolation.
 */
export function generateSpiralLerp(clusterPosition: Vector3): Parametric {
  const clusterNormal = clusterPosition.clone().normalize();
  const clusterTangent = new Vector3(0, 1, 0).cross(clusterNormal).normalize();
  const clusterBitangent = clusterNormal.clone().cross(clusterTangent).normalize();

  const tilesPerSpiralWinding = 4.66;
  const distanceAlongAxisPerSpiral = 6;

  return (t: number) => {
    const radiusBonus = 0.05 * t;
    const radius = 3 + radiusBonus;
    const angle = t * 2 * Math.PI / (tilesPerSpiralWinding + radiusBonus * 0.5);
    const distanceAlongAxis = t * distanceAlongAxisPerSpiral / (tilesPerSpiralWinding + radiusBonus);

    const stalkPosition = clusterNormal.clone().multiplyScalar(distanceAlongAxis)
        .add(clusterTangent.clone().multiplyScalar(Math.cos(angle) * radius))
        .add(clusterBitangent.clone().multiplyScalar(Math.sin(angle) * radius));

    return stalkPosition.add(clusterPosition);
  }
}
